import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const MC_SOURCE = 'https://www.w3.org/TR/media-capabilities/';

/**
 * navigator.mediaCapabilities.decodingInfo — a deeper codec probe than
 * canPlayType.
 *
 * canPlayType answers "can this element play it at all". decodingInfo answers
 * "can it decode/smoothly/keep up at this resolution and framerate", which
 * depends on the hardware pipeline and power profile. The four booleans
 * (supported / smooth / powerEfficient) form a much sharper fingerprint than
 * the 'probably'/'maybe' strings, and they expose whether a decoder is
 * hardware-accelerated — a genuine software-vs-hardware tell that canPlayType
 * cannot see.
 */
export const mediaCapabilitiesProbe: Probe = {
  id: 'mediaCapabilities',
  label: 'Media capabilities',
  layer: 'media',
  tier: 'client',
  category: 'hardware',
  description: 'decodingInfo / encodingInfo smoothness + power efficiency',
  sources: [MC_SOURCE],
  budgetMs: 6000,

  async run(): Promise<ProbeResult> {
    const mc = (navigator as Navigator & { mediaCapabilities?: MediaCapabilitiesLike })
      .mediaCapabilities;
    if (!mc?.decodingInfo) {
      return {
        signals: {
          supported: na<boolean>('unsupported', 'client', 'navigator.mediaCapabilities absent'),
        },
      };
    }

    // A matrix of realistic configurations; the smooth/powerEfficient flags
    // are where the hardware signal lives.
    const configs: Array<{ key: string; contentType: string; type: string }> = [
      { key: 'h264-720p', contentType: 'video/mp4; codecs="avc1.42E01E"', type: 'file' },
      { key: 'h264-1080p', contentType: 'video/mp4; codecs="avc1.640028"', type: 'file' },
      { key: 'h264-4k', contentType: 'video/mp4; codecs="avc1.640033"', type: 'file' },
      { key: 'vp8-1080p', contentType: 'video/webm; codecs="vp8"', type: 'file' },
      { key: 'vp9-1080p', contentType: 'video/webm; codecs="vp09.00.10.08"', type: 'file' },
      { key: 'av1-1080p', contentType: 'video/mp4; codecs="av01.0.05M.08"', type: 'file' },
      { key: 'h265-1080p', contentType: 'video/mp4; codecs="hvc1.1.6.L93.B0"', type: 'file' },
      { key: 'opus-audio', contentType: 'audio/mp4; codecs="mp4a.40.2"', type: 'file' },
      { key: 'mp3-audio', contentType: 'audio/mpeg', type: 'file' },
    ];

    const results: Record<string, Record<string, boolean>> = {};
    for (const c of configs) {
      const row: Record<string, boolean> = { supported: false, smooth: false, powerEfficient: false };
      try {
        const info = await mc.decodingInfo({
          type: c.type,
          video: {
            contentType: c.contentType,
            width: 1920,
            height: 1080,
            bitrate: 5_000_000,
            framerate: 30,
          },
        } as MediaDecodingConfiguration);
        row.supported = !!info.supported;
        row.smooth = !!info.smooth;
        row.powerEfficient = !!info.powerEfficient;
      } catch {
        /* leave defaults */
      }
      results[c.key] = row;
    }

    // Encoding: WebM VP8 is commonly hardware-accelerated where decoding is not
    // (and vice versa), so the asymmetry is itself informative.
    const encoding: Record<string, boolean> = { supported: false, smooth: false, powerEfficient: false };
    try {
      const info = await mc.encodingInfo?.({
        type: 'record',
        video: {
          contentType: 'video/webm; codecs="vp8"',
          width: 1280,
          height: 720,
          bitrate: 2_500_000,
          framerate: 30,
        },
      } as MediaEncodingConfiguration);
      if (info) {
        encoding.supported = !!info.supported;
        encoding.smooth = !!info.smooth;
        encoding.powerEfficient = !!info.powerEfficient;
      }
    } catch {
      /* ignore */
    }

    // Count how many configs report powerEfficient — a strong proxy for how
    // much of the decode path is hardware-backed.
    const hwBacked = Object.values(results).filter((r) => r.powerEfficient).length;

    return {
      signals: {
        supported: ok(true, 'client'),
        decoding: ok(results, 'client'),
        encodingVp8: ok(encoding, 'client'),
        powerEfficientCount: ok(hwBacked, 'derived'),
        smoothCount: ok(Object.values(results).filter((r) => r.smooth).length, 'derived'),
      },
      parts: {
        media: {
          // The smooth/powerEfficient matrix is a stable capability fingerprint
          // (unlike decode latency, which we deliberately keep out of parts).
          mediaCapabilities: results,
          encodingVp8: encoding,
        },
      },
    };
  },
};

interface MediaCapabilitiesLike {
  decodingInfo?: (c: unknown) => Promise<{ supported: boolean; smooth: boolean; powerEfficient: boolean }>;
  encodingInfo?: (c: unknown) => Promise<{ supported: boolean; smooth: boolean; powerEfficient: boolean }>;
}
type MediaDecodingConfiguration = { type: string; video: unknown };
type MediaEncodingConfiguration = { type: string; video: unknown };

registerProbe(mediaCapabilitiesProbe);
